import { named, withDependencies } from '@wix/thunderbolt-ioc'
import fastdom from 'fastdom'
import type {
	BrowserWindow,
	Experiments } from '@wix/thunderbolt-symbols'
import {
	BrowserWindowSymbol,
	ExperimentsSymbol,
	MasterPageFeatureConfigSymbol,
	PageFeatureConfigSymbol,
} from '@wix/thunderbolt-symbols'
import { constants } from '@wix/thunderbolt-becky-root'
import { name } from './symbols'
import type {
	IStickyToComponent,
	SCROLL_DIRECTION,
	StickyToComponentMasterPageConfig,
	StickyToComponentPageConfig,
} from './types'

const { SITE_CONTAINER_WITH_HEADER_SELECTOR } = constants

export const StickyToComponent = withDependencies(
	[
		named(PageFeatureConfigSymbol, name),
		named(MasterPageFeatureConfigSymbol, name),
		BrowserWindowSymbol,
		ExperimentsSymbol,
	],
	(
		pageConfig: StickyToComponentPageConfig,
		masterPageConfig: StickyToComponentMasterPageConfig,
		browserWindow: NonNullable<BrowserWindow>,
		experiments: Experiments
	): IStickyToComponent => {
		let headerResizeObserver: ResizeObserver
		let scrollDirection: SCROLL_DIRECTION
		let scrollY = browserWindow.scrollY
		let measuredHeaderHeight: number = 0

		const getHeaderElement = () => Array.from(browserWindow.document.getElementsByTagName('header'))[0]

		const isHeaderElementExists = () => !!getHeaderElement()

		const initiateHeaderHeightVar = () => {
			const header = getHeaderElement()
			fastdom.measure(() => {
				measuredHeaderHeight = header.getBoundingClientRect().height
				setHeaderHeightVar(measuredHeaderHeight)
			})
		}

		const setHeaderHeightVar = (height: number | string) => {
			fastdom.mutate(() => {
				const siteContainer = browserWindow.document.querySelector(
					SITE_CONTAINER_WITH_HEADER_SELECTOR
				) as HTMLElement
				siteContainer && siteContainer.style.setProperty('--header-height', `${height}px`)
			})
		}

		const observeHeaderHeight = () => {
			const header = getHeaderElement()
			headerResizeObserver = new ResizeObserver(() => {
				fastdom.measure(() => {
					measuredHeaderHeight = header.getBoundingClientRect().height
					setHeaderHeightVar(measuredHeaderHeight)
				})
			})
			headerResizeObserver.observe(header)
		}

		const transitionStartHandler = () => {
			scrollY = browserWindow.scrollY
		}

		const transitionEndHandler = () => {
			if (experiments['specs.thunderbolt.newTransitionEndHandlerLogic']) {
				const isTransitionInStudio = pageConfig.isHeaderSectionHasAnimationBehavior
				const transitionedElement = isTransitionInStudio ? getHeaderElement().children[0] : getHeaderElement()

				const transitionedElementTop = transitionedElement.getBoundingClientRect().top

				const transitionedElementOpacity = browserWindow.getComputedStyle(transitionedElement).opacity

				setHeaderHeightVar(
					transitionedElementTop < 0 || transitionedElementOpacity === '0' ? 0 : measuredHeaderHeight
				)
			} else {
				const newScrollY = browserWindow.scrollY

				if (!scrollDirection && !newScrollY) {
					return
				}

				if (scrollY === newScrollY) {
					return
				}

				scrollDirection = scrollY > newScrollY ? 'UP' : 'DOWN'

				setHeaderHeightVar(scrollDirection === 'UP' ? measuredHeaderHeight : 0)
			}
		}

		// TODO: remove event listeners to 'transitionstart' when merging the newTransitionEndHandlerLogic experiment.
		const listenToTransition = () => {
			const header = getHeaderElement()
			header.addEventListener('transitionstart', transitionStartHandler)
			header.addEventListener('transitionend', transitionEndHandler)
		}

		const stopListenToTransition = () => {
			const header = getHeaderElement()
			header.removeEventListener('transitionstart', transitionEndHandler)
			header.removeEventListener('transitionend', transitionEndHandler)
		}

		const unobserveHeaderHeight = () => {
			const header = getHeaderElement()
			headerResizeObserver.unobserve(header)
		}

		const shouldListenToTransition = (): boolean => {
			return masterPageConfig.shouldListenToTransitionEnd || pageConfig.isHeaderSectionHasAnimationBehavior
		}

		return {
			initHeaderObserver() {
				if (!isHeaderElementExists()) {
					return
				}

				initiateHeaderHeightVar()
				observeHeaderHeight()

				if (shouldListenToTransition()) {
					listenToTransition()
				}
			},
			destroyHeaderObserver() {
				if (!isHeaderElementExists()) {
					return
				}

				unobserveHeaderHeight()

				if (shouldListenToTransition()) {
					stopListenToTransition()
				}
			},
			restoreHeaderHeightVar() {
				if (shouldListenToTransition()) {
					setHeaderHeightVar(measuredHeaderHeight)
				}
			},
		}
	}
)
